import { type ReactNode, useMemo } from "react";

import clsx from "clsx";
import { upperFirst } from "lodash";
import { twMerge } from "tailwind-merge";

import { useUser } from "@components/user-context";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type TileDescriptor } from "@features/metrics-explorer/types";
import { isTileOverriden } from "@features/metrics-explorer/utils";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";

import { OverridesTooltip } from "./overrides-tooltip";

interface Props {
  tile: TileDescriptor;
  onTooltipOpen(): void;
  onTooltipClose(): void;
}

export const OverrideTags = ({
  tile,
  onTooltipOpen,
  onTooltipClose,
}: Props) => {
  const { user } = useUser();

  const {
    filters: { accountingMode, dateRangeInterval, timeGranularity },
  } = useMetricsExplorerContext();

  const overrides = useMemo(() => {
    const result: {
      label: string;
      noTruncate?: boolean;
      className?: string;
    }[] = [];

    const accMode = tile.accountingMode ?? accountingMode;

    result.push({
      label: upperFirst(accMode),
      noTruncate: true,
      className:
        accMode === "accrual"
          ? "bg-v2-highlight-information border-v2-light text-v2-action"
          : "bg-v2-highlight-notice border-v2-notice text-v2-action-positive",
    });

    if (tile.attributionModel) {
      result.push({
        label: user.attributionMethodEnum.getLabel(tile.attributionModel),
      });
    }

    if (tile.attributionWindow) {
      result.push({
        label: `${tile.attributionWindow} day`,
      });
    }

    if (tile.compareDateRange) {
      const compareDateRangeInterval = deriveComparisonDateRange(
        dateRangeInterval,
        tile.compareDateRange,
        user.timezone,
        timeGranularity,
      );

      result.push({
        label: compareDateRangeInterval
          ? formatDateRange(compareDateRangeInterval)
          : "",
      });
    }

    if (tile.filterQuery) {
      result.push({ label: tile.filterQuery.query });
    }

    if (tile.advancedSearch) {
      result.push({
        label: "Advanced Search",
      });
    }

    if (tile.filters.length > 0) {
      const length = tile.filters.length;
      result.push({
        label: `${length} ${length === 1 ? "filter" : "filters"}`,
      });
    }

    return result;
  }, [tile, user, accountingMode, dateRangeInterval, timeGranularity]);

  const visibleOverrides = overrides.slice(0, 3);

  return (
    <OverridesTooltip
      showTooltip={isTileOverriden(tile)}
      onOpen={onTooltipOpen}
      onClose={onTooltipClose}
      tile={tile}
    >
      <div className="flex items-center gap-1 mt-[1px]">
        {visibleOverrides.map((e) => (
          <Tag key={e.label} noTruncate={e.noTruncate} className={e.className}>
            {e.label}
          </Tag>
        ))}
        {overrides.length > 3 && (
          <Tag noTruncate>+{overrides.length - 3} more</Tag>
        )}
      </div>
    </OverridesTooltip>
  );
};

const Tag = ({
  children,
  noTruncate,
  className,
}: {
  children: ReactNode;
  noTruncate?: boolean;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        clsx(
          "text-[9px] bg-v2-action-tertiary border border-v2-secondary rounded px-1 py-[1px] text-v2-primary",
          "leading-3 whitespace-nowrap max-w-[200px]",
          { truncate: !noTruncate },
        ),
        className,
      )}
    >
      {children}
    </div>
  );
};
